// Apolo IDE — Plan tab root view (sprint plan-tab-v1, E2 canvas shell).
//
// Layout (desktop): sidebar 280px | canvas (flex-1 + toolbar inferior overlay)
// Layout (mobile):  drawer + canvas full-width.
//
// O canvas é a primeira-class citizen — modo document virá em sprint futura.

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAgentStore } from '../../store/useAgentStore.js';
import { usePlanList } from '../../hooks/usePlanList.js';
import PlanSidebar from './PlanSidebar.jsx';
import PlanCanvas from './PlanCanvas.jsx';
import PlanEmptyCanvas from './PlanEmptyCanvas.jsx';
import PlanCreateModal from './PlanCreateModal.jsx';
import MobileDrawer from '../MobileDrawer.jsx';

export default function PlanView() {
  const activePlanId = useAgentStore((s) => s.plan.activePlanId);
  const setActivePlan = useAgentStore((s) => s.setActivePlan);
  const sidebarCollapsed = useAgentStore((s) => s.plan.ui.sidebarCollapsed);
  const setPlanUiPref = useAgentStore((s) => s.setPlanUiPref);
  const { plans, loading, error, refresh, create, remove, patch } = usePlanList();
  const [createOpen, setCreateOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Hamburger global -> abre drawer mobile.
  useEffect(() => {
    function handler() { setDrawerOpen(true); }
    window.addEventListener('apolo:openMobileDrawer', handler);
    return () => window.removeEventListener('apolo:openMobileDrawer', handler);
  }, []);

  // Seleciona automaticamente o primeiro plano se nenhum selecionado.
  useEffect(() => {
    if (!activePlanId && plans.length > 0) {
      setActivePlan(plans[0].id);
    }
  }, [activePlanId, plans, setActivePlan]);

  async function handleCreate(fields) {
    const res = await create(fields);
    if (res.ok && res.plan) {
      setActivePlan(res.plan.id);
      setCreateOpen(false);
    }
    return res;
  }

  const activePlan = plans.find((p) => p.id === activePlanId) || null;

  return (
    <div
      id="tabpanel-plan"
      role="tabpanel"
      aria-labelledby="tab-plan"
      className="relative flex h-full w-full flex-1 overflow-hidden bg-apolo-bg text-apolo-textPrimary"
    >
      {/* Sidebar desktop */}
      <div className={`hidden lg:flex shrink-0 transition-[width] duration-300 ${sidebarCollapsed ? 'w-0' : 'w-[280px]'} overflow-hidden`}>
        <PlanSidebar
          plans={plans}
          activePlanId={activePlanId}
          loading={loading}
          error={error}
          onSelect={setActivePlan}
          onCreate={() => setCreateOpen(true)}
          onRefresh={refresh}
          onDelete={remove}
          onPatch={patch}
        />
      </div>

      {/* Sidebar mobile drawer */}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Planos">
        <PlanSidebar
          plans={plans}
          activePlanId={activePlanId}
          loading={loading}
          error={error}
          onSelect={(id) => { setActivePlan(id); setDrawerOpen(false); }}
          onCreate={() => { setCreateOpen(true); setDrawerOpen(false); }}
          onRefresh={refresh}
          onDelete={remove}
          onPatch={patch}
          mobile
        />
      </MobileDrawer>

      {/* Botão collapse sidebar desktop */}
      <button
        type="button"
        onClick={() => setPlanUiPref('sidebarCollapsed', !sidebarCollapsed)}
        aria-label={sidebarCollapsed ? 'Mostrar painel de planos' : 'Esconder painel de planos'}
        className="hidden lg:flex absolute left-0 top-3 z-30 h-9 w-9 -translate-x-0 translate-x-1 items-center justify-center rounded-md text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-plan-accentRing"
        style={{ left: sidebarCollapsed ? 6 : 268 }}
      >
        <ChevronGlyph direction={sidebarCollapsed ? 'right' : 'left'} />
      </button>

      {/* Canvas area */}
      <div className="relative flex flex-1 overflow-hidden">
        <AnimatePresence mode="wait">
          {activePlan ? (
            <motion.div
              key={activePlan.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="flex flex-1"
            >
              <PlanCanvas plan={activePlan} onRenamePlan={(name) => patch(activePlan.id, { name })} />
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="flex flex-1"
            >
              <PlanEmptyCanvas hasPlans={plans.length > 0} onCreate={() => setCreateOpen(true)} loading={loading} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <PlanCreateModal open={createOpen} onClose={() => setCreateOpen(false)} onCreate={handleCreate} />
    </div>
  );
}

function ChevronGlyph({ direction = 'left' }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {direction === 'left'
        ? <polyline points="15 18 9 12 15 6" />
        : <polyline points="9 18 15 12 9 6" />}
    </svg>
  );
}
